// ─── Week and day time grid ──────────────────────────────────────────────────
//
// One component for both views: a day is a week with one column. The design
// doc treats them as one pattern ("Month/Week/Day tabs"), and splitting them
// would mean two places to fix every time-arithmetic bug.
//
// "Light, unobtrusive time grid, Monday as the default first day of the week,
// rounded-corner appointment blocks whose height reflects actual duration."
//
// ── Height reflects duration, and that is load-bearing ──────────────────────
//
// A two-hour colour must LOOK twice as long as a one-hour cut. It is the only
// thing that lets somebody read a day's shape at a glance, and it is why the
// booking record stores its own end time rather than deriving it from a
// service whose duration may since have changed.

import { useMemo, useRef, useState } from "react";
import { assignLanes, clampToWindow, minutesIntoDay } from "./layout.js";
import { proposeDrag, collidesWith, type DragMode } from "./drag.js";
import type { BookingStatus } from "./types.js";

export type GridBooking = {
  id: string;
  startsAt: Date;
  endsAt: Date;
  status: BookingStatus;
  personName?: string | null;
  serviceName?: string | null;
  blockReason?: string | null;
};

export type GridDay = {
  date: Date;
  /** Working periods in minutes from midnight. Anything outside these is
   *  drawn as closed, so non-working time reads as unavailable rather than as
   *  merely empty. */
  working: { startMinute: number; endMinute: number }[];
  bookings: GridBooking[];
};

export type TimeGridProps = {
  days: GridDay[];
  /** Visible window. Defaults to a working-day span rather than midnight to
   *  midnight, because eight empty night-time hours push the real day off the
   *  screen. */
  startHour?: number;
  endHour?: number;
  pixelsPerMinute?: number;
  onOpen?: (bookingId: string) => void;
  /** Pressing empty space is how a booking gets made or time gets blocked. */
  onPickEmpty?: (date: Date, minute: number) => void;
  /**
   * Commit a drag. THIS IS A RESCHEDULE, and it can refuse — the slot may have
   * been taken between the grab and the release, and the booking module raises
   * rather than double-book.
   *
   * Resolve and the block settles into its new place. Reject and it returns to
   * where it was, with `onDragRefused` called. The block is never shown in a
   * position that has not been confirmed.
   */
  onMove?: (bookingId: string, startsAt: Date, endsAt: Date) => Promise<void>;
  /** Same contract, for dragging the bottom edge. */
  onResize?: (bookingId: string, endsAt: Date) => Promise<void>;
  /** Called when a drag was refused — by the local conflict check before
   *  sending, or by the server after. The message is for the person. */
  onDragRefused?: (bookingId: string, reason: string) => void;
};

type DragState = {
  id: string;
  mode: DragMode;
  originY: number;
  deltaPixels: number;
  /** True once the change has been sent and is awaiting confirmation. */
  committing: boolean;
};

const STATUS_LABEL: Record<BookingStatus, string> = {
  requested: "Requested", confirmed: "Confirmed", declined: "Declined",
  cancelled: "Cancelled", reschedule_requested: "Reschedule requested",
  expired: "Expired", blocked: "Blocked",
};

const hhmm = (minute: number) =>
  `${String(Math.floor(minute / 60)).padStart(2, "0")}:${String(minute % 60).padStart(2, "0")}`;

export function TimeGrid({
  days, startHour = 7, endHour = 20, pixelsPerMinute = 0.9,
  onOpen, onPickEmpty, onMove, onResize, onDragRefused,
}: TimeGridProps) {
  const startMinute = startHour * 60;
  const endMinute = endHour * 60;
  const [drag, setDrag] = useState<DragState | null>(null);
  /**
   * The drag, readable and writable SYNCHRONOUSLY.
   *
   * A real bug, found by the resize test: a pointerup on the grip runs the
   * grip's handler and then bubbles to the block's, so ONE release produced
   * TWO reschedules. The module would have applied them as two separate moves,
   * and they carry no shared client action id, so replay protection would not
   * have collapsed them.
   *
   * React state updates asynchronously, so a guard reading `drag` cannot stop a
   * second handler in the same tick. This ref updates immediately, and every
   * guard reads it; `drag` is only for rendering.
   *
   * There are two independent defences — this ref, and the grip stopping its
   * own events from bubbling. Verified by removing them: either alone prevents
   * the double call, and only removing BOTH reproduces the bug. Keeping both is
   * deliberate, because they fail differently — the ref survives a future
   * handler added elsewhere, and stopPropagation avoids the duplicate dispatch
   * in the first place.
   */
  const dragRef = useRef<DragState | null>(null);
  function setDragState(next: DragState | null) {
    dragRef.current = next;
    setDrag(next);
  }

  const draggable = !!onMove || !!onResize;
  const allBookings = useMemo(() => days.flatMap(d => d.bookings), [days]);

  function beginDrag(e: React.PointerEvent, id: string, mode: DragMode) {
    if (!draggable) return;
    // Only the primary button, and never while a commit is in flight — a
    // second drag over an unconfirmed one would race two reschedules of the
    // same booking.
    if (e.button !== 0 || dragRef.current?.committing) return;
    e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    setDragState({ id, mode, originY: e.clientY, deltaPixels: 0, committing: false });
  }

  function moveDrag(e: React.PointerEvent) {
    const current = dragRef.current;
    if (!current || current.committing) return;
    setDragState({ ...current, deltaPixels: e.clientY - current.originY });
  }

  /**
   * Where the block being dragged would actually land, right now.
   *
   * ── Why this exists (issue #6) ────────────────────────────────────────────
   *
   * The block followed the pointer but its label kept showing the time it was
   * ALREADY at. So the one question a drag asks — "what time is this now?" —
   * was the one thing on screen that did not answer it, and the person had to
   * drop it and read the result to find out.
   *
   * ── Why it calls `proposeDrag` rather than doing its own arithmetic ───────
   *
   * Because a label that disagrees with what gets saved is worse than no
   * label. Same function, same snapping, same clamping as `endDrag`: if this
   * says 10:15 then 10:15 is what will be sent. Duplicating the sums here is
   * exactly how the two would drift.
   *
   * `collidesWith` is the same courtesy check `endDrag` runs, so a drop that
   * is going to be refused says so while the finger is still down instead of
   * after it lifts.
   */
  const livePreview = useMemo(() => {
    if (!drag || drag.committing) return null;
    const booking = allBookings.find(b => b.id === drag.id);
    if (!booking) return null;
    const proposal = proposeDrag(
      booking, drag.mode, drag.deltaPixels, pixelsPerMinute,
      { windowStartMinute: startMinute, windowEndMinute: endMinute },
    );
    return {
      id: drag.id,
      proposal,
      moved: proposal.deltaMinutes !== 0,
      clashes: collidesWith(proposal, allBookings, drag.id),
    };
  }, [drag, allBookings, pixelsPerMinute, startMinute, endMinute]);

  async function endDrag() {
    const current = dragRef.current;
    if (!current || current.committing) return;

    // Claim the gesture before anything else, so a second handler entering in
    // the same tick returns immediately. `setDragState` further down would also
    // claim it, but only after the proposal work — this covers the whole
    // function, including anything asynchronous added to it later.
    dragRef.current = { ...current, committing: true };

    const booking = allBookings.find(b => b.id === current.id);
    if (!booking) { setDragState(null); return; }

    const proposal = proposeDrag(
      booking, current.mode, current.deltaPixels, pixelsPerMinute,
      { windowStartMinute: startMinute, windowEndMinute: endMinute },
    );

    // Ended where it began: nothing happened, so nothing is sent and nothing
    // is claimed.
    if (proposal.deltaMinutes === 0) { setDragState(null); return; }

    // A courtesy check, so an obvious clash is refused without a round trip.
    // NOT the guarantee — the database is — and it cannot see a booking
    // somebody else made a moment ago.
    //
    // That last sentence is proven rather than asserted: the browser suite's
    // "refuses a drop the database will not accept" renders the grid, creates
    // a clashing booking on the server behind the page's back, and drops onto
    // it. This check passes on the stale snapshot, the request goes, and the
    // exclusion constraint is what refuses it. The test matches on the
    // SERVER's wording specifically, so it cannot pass by this branch firing.
    if (collidesWith(proposal, allBookings, current.id)) {
      setDragState(null);
      onDragRefused?.(current.id, "That time is already booked.");
      return;
    }

    setDragState({ ...current, committing: true });
    try {
      if (current.mode === "move") await onMove?.(current.id, proposal.startsAt, proposal.endsAt);
      else await onResize?.(current.id, proposal.endsAt);
      // The parent now holds the new time and will re-render with it. Clearing
      // here rather than moving the block ourselves is what keeps the calendar
      // showing only confirmed state.
      setDragState(null);
    } catch (err) {
      setDragState(null);
      onDragRefused?.(
        current.id,
        err instanceof Error ? err.message : "That change could not be saved.",
      );
    }
  }
  const height = (endMinute - startMinute) * pixelsPerMinute;

  const hours = useMemo(() => {
    const out: number[] = [];
    for (let m = startMinute; m <= endMinute; m += 60) out.push(m);
    return out;
  }, [startMinute, endMinute]);

  return (
    <div
      data-testid="time-grid"
      style={{
        display: "grid",
        gridTemplateColumns: `56px repeat(${days.length}, minmax(0, 1fr))`,
        gap: "var(--nl-tile-gap)",
        background: "var(--nl-surface)",
        border: "1px solid var(--nl-stroke)",
        borderRadius: "var(--nl-radius-card)",
        padding: 12,
      }}
    >
      {/* Column headers. The gutter's header is empty but present, so the
          columns line up without absolute positioning. */}
      <div />
      {days.map(d => (
        <div key={`h-${d.date.toISOString()}`} style={{ textAlign: "center", paddingBottom: 6 }}>
          <div style={{ fontSize: "0.68rem", letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--nl-ink-faint)" }}>
            {d.date.toLocaleDateString("en-GB", { weekday: "short" })}
          </div>
          <div style={{ fontFamily: "var(--nl-font-num)", fontVariantNumeric: "tabular-nums", fontSize: "1.05rem", color: "var(--nl-ink)" }}>
            {d.date.getDate()}
          </div>
        </div>
      ))}

      {/* Hour gutter. Labels sit at the line rather than in the middle of the
          band, so a block starting at 10:00 lines up with the text saying so. */}
      <div style={{ position: "relative", height }}>
        {hours.map(m => (
          <div
            key={m}
            style={{
              position: "absolute", top: (m - startMinute) * pixelsPerMinute, right: 6,
              transform: "translateY(-50%)",
              fontSize: "0.7rem", color: "var(--nl-ink-faint)",
              fontFamily: "var(--nl-font-num)", fontVariantNumeric: "tabular-nums",
            }}
          >{hhmm(m)}</div>
        ))}
      </div>

      {days.map(day => {
        const placed = assignLanes(day.bookings);
        return (
          <div
            key={day.date.toISOString()}
            data-testid="grid-day"
            style={{
              position: "relative", height,
              background: "var(--nl-state-blocked)",
              borderRadius: "var(--nl-radius-tile)",
              overflow: "hidden",
            }}
          >
            {/* Working periods, drawn as the lighter ground. Everything not
                covered stays the blocked colour, so closed time is visibly
                closed rather than merely empty. */}
            {day.working.map((w, i) => {
              const top = Math.max(0, w.startMinute - startMinute) * pixelsPerMinute;
              const bottom = Math.min(endMinute, w.endMinute);
              const h = Math.max(0, bottom - Math.max(startMinute, w.startMinute)) * pixelsPerMinute;
              if (h <= 0) return null;
              return (
                <div
                  key={i}
                  data-testid="working-band"
                  aria-hidden="true"
                  style={{ position: "absolute", left: 0, right: 0, top, height: h, background: "var(--nl-surface-raised)" }}
                />
              );
            })}

            {/* Hour lines — light and unobtrusive, per the doc. */}
            {hours.map(m => (
              <div
                key={m}
                aria-hidden="true"
                style={{
                  position: "absolute", left: 0, right: 0,
                  top: (m - startMinute) * pixelsPerMinute,
                  borderTop: "1px solid var(--nl-stroke-soft)", opacity: 0.5,
                }}
              />
            ))}

            {/* Pressing empty space creates. Behind the blocks, so it never
                steals a click meant for a booking. */}
            {onPickEmpty && (
              <button
                type="button"
                aria-label={`Add on ${day.date.toDateString()}`}
                onClick={e => {
                  const box = (e.currentTarget as HTMLElement).getBoundingClientRect();
                  const minute = startMinute + Math.round((e.clientY - box.top) / pixelsPerMinute / 15) * 15;
                  onPickEmpty(day.date, Math.min(endMinute, Math.max(startMinute, minute)));
                }}
                style={{
                  position: "absolute", inset: 0, background: "transparent",
                  border: 0, padding: 0, cursor: "copy",
                }}
              />
            )}

            {placed.map(b => {
              const box = clampToWindow(b, startMinute, endMinute);
              if (!box) return null;
              const isBlock = b.status === "blocked";
              const width = `calc(${100 / b.lanes}% - 4px)`;
              const left = `calc(${(100 / b.lanes) * b.lane}% + 2px)`;
              const minutes = Math.round((b.endsAt.getTime() - b.startsAt.getTime()) / 60000);

              return (
                <button
                  key={b.id}
                  type="button"
                  data-testid="grid-booking"
                  data-status={b.status}
                  data-lanes={b.lanes}
                  aria-label={
                    `${hhmm(minutesIntoDay(b.startsAt))}–${hhmm(minutesIntoDay(b.endsAt))}, ` +
                    `${minutes} minutes, ${STATUS_LABEL[b.status]}` +
                    `${isBlock ? `: ${b.blockReason ?? "Blocked"}` : b.personName ? `, ${b.personName}` : ""}`
                  }
                  onClick={() => { if (!drag) onOpen?.(b.id); }}
                  onPointerDown={e => beginDrag(e, b.id, "move")}
                  onPointerMove={moveDrag}
                  onPointerUp={endDrag}
                  onPointerCancel={() => setDragState(null)}
                  data-dragging={drag?.id === b.id && !drag.committing ? "true" : undefined}
                  data-committing={drag?.id === b.id && drag.committing ? "true" : undefined}
                  style={{
                    position: "absolute",
                    // While dragging, the block follows the pointer as a
                    // PREVIEW. It is never written into the record until the
                    // parent confirms, so what is shown here and what is true
                    // cannot diverge.
                    transform: drag?.id === b.id && !drag.committing
                      ? `translateY(${drag.deltaPixels}px)` : undefined,
                    // In flight: visibly unsettled, so nobody reads an
                    // unconfirmed move as done.
                    opacity: drag?.id === b.id && drag.committing ? 0.55 : 1,
                    touchAction: draggable ? "none" : undefined,
                    top: box.topMinute * pixelsPerMinute,
                    // Height IS the duration. The floor keeps a 15-minute
                    // appointment legible without letting it lie about length.
                    height: Math.max(18, box.heightMinutes * pixelsPerMinute - 2),
                    left, width,
                    borderRadius: "var(--nl-radius-tile)",
                    border: "1px solid var(--nl-stroke)",
                    borderLeft: `3px solid ${isBlock ? "var(--nl-state-blocked-ink)" : `var(--nl-status-${b.status})`}`,
                    background: isBlock ? "var(--nl-state-blocked)" : "var(--nl-state-full)",
                    color: isBlock ? "var(--nl-state-blocked-ink)" : "var(--nl-state-full-ink)",
                    padding: "4px 6px",
                    textAlign: "left", font: "inherit", cursor: onOpen ? "pointer" : "default",
                    overflow: "hidden", display: "flex", flexDirection: "column", gap: 2,
                  }}
                >
                  {(() => {
                    // While this block is the one being dragged, its time
                    // reads as where it would LAND — the whole question a drag
                    // asks. Otherwise it reads as where it is.
                    const live = livePreview?.id === b.id ? livePreview : null;
                    const from = live ? live.proposal.startsAt : b.startsAt;
                    const to = live ? live.proposal.endsAt : b.endsAt;
                    return (
                      <span
                        data-testid={live ? "drag-time" : undefined}
                        data-clashes={live?.clashes ? "true" : undefined}
                        style={{
                          fontSize: "0.68rem",
                          fontFamily: "var(--nl-font-num)",
                          fontVariantNumeric: "tabular-nums",
                          opacity: live ? 1 : 0.85,
                          fontWeight: live ? 700 : undefined,
                          // A drop that is going to be refused says so now,
                          // rather than after the finger lifts.
                          color: live?.clashes ? "var(--nl-status-declined)" : undefined,
                        }}
                      >
                        {live
                          ? `${hhmm(minutesIntoDay(from))}–${hhmm(minutesIntoDay(to))}${live.clashes ? " ✕" : ""}`
                          : hhmm(minutesIntoDay(b.startsAt))}
                      </span>
                    );
                  })()}
                  <span style={{ fontSize: "0.78rem", lineHeight: 1.2 }}>
                    {isBlock ? (b.blockReason ?? "Blocked") : (b.personName ?? "Client")}
                  </span>
                  {/* Only when there is genuinely room — a label that overflows
                      its block is worse than one that is absent. */}
                  {!isBlock && b.serviceName && box.heightMinutes >= 45 && (
                    <span style={{ fontSize: "0.7rem", opacity: 0.8, lineHeight: 1.2 }}>{b.serviceName}</span>
                  )}
                  {b.status === "requested" && box.heightMinutes >= 60 && (
                    <span style={{ fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "0.05em", opacity: 0.9 }}>
                      Requested
                    </span>
                  )}

                  {/* Resize grip. Only the END moves — the start is when the
                      customer is expected, and dragging the bottom edge must
                      not quietly change their arrival time. */}
                  {onResize && (
                    <span
                      data-testid="resize-grip"
                      data-booking={b.id}
                      role="presentation"
                      onPointerDown={e => beginDrag(e, b.id, "resize")}
                      onPointerMove={e => { e.stopPropagation(); moveDrag(e); }}
                      onPointerUp={e => { e.stopPropagation(); void endDrag(); }}
                      style={{
                        position: "absolute", left: 0, right: 0, bottom: 0, height: 8,
                        cursor: "ns-resize", touchAction: "none",
                      }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
